import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logStorageAccessError } from '@/lib/securityLogger';

interface UseUnifiedSignedUrlOptions {
  bucket: string;
  path: string | null;
  enabled?: boolean;
  expiryDays?: number;
}

const CACHE_PREFIX = 'signed_url_cache_';
const EXPIRY_BUFFER_HOURS = 24;

export const useUnifiedSignedUrl = ({
  bucket,
  path,
  enabled = true,
  expiryDays = 7,
}: UseUnifiedSignedUrlOptions) => {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getCacheKey = useCallback(() => {
    return `${CACHE_PREFIX}${bucket}_${path}`;
  }, [bucket, path]);

  const getCachedUrl = useCallback(() => {
    if (!path) return null;
    
    try {
      const cacheKey = getCacheKey();
      const cached = localStorage.getItem(cacheKey);
      
      if (cached) {
        const { url: cachedUrl, expiresAt } = JSON.parse(cached);
        const expiryDate = new Date(expiresAt);
        const now = new Date();
        const hoursUntilExpiry = (expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60);
        
        if (hoursUntilExpiry > EXPIRY_BUFFER_HOURS) {
          return cachedUrl;
        }
      }
    } catch (err) {
      console.error('[CACHE] Error reading cache:', err);
    }
    
    return null;
  }, [path, getCacheKey]);

  const setCachedUrl = useCallback((signedUrl: string, expiresAt: Date) => {
    if (!path) return;
    
    try {
      const cacheKey = getCacheKey();
      localStorage.setItem(cacheKey, JSON.stringify({
        url: signedUrl,
        expiresAt: expiresAt.toISOString(),
      }));
    } catch (err) {
      console.error('[CACHE] Error writing cache:', err);
    }
  }, [path, getCacheKey]);

  const generateSignedUrl = useCallback(async (retryCount = 0): Promise<string | null> => {
    if (!path) return null;
    
    try {
      const expirySeconds = expiryDays * 24 * 60 * 60;
      const { data, error: signedUrlError } = await supabase.storage
        .from(bucket)
        .createSignedUrl(path, expirySeconds);

      if (signedUrlError) throw signedUrlError;
      if (!data?.signedUrl) throw new Error('No signed URL returned');

      const expiresAt = new Date(Date.now() + expirySeconds * 1000);
      setCachedUrl(data.signedUrl, expiresAt);

      return data.signedUrl;
    } catch (err: any) {
      console.error('[SIGNED_URL] Error generating:', err);

      // Retry logic with exponential backoff
      if (retryCount < 3) {
        const delay = Math.pow(2, retryCount) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
        return generateSignedUrl(retryCount + 1);
      }

      // Log error after retries exhausted
      await logStorageAccessError({
        operation: 'read',
        bucket,
        path,
        errorMessage: err.message,
      });

      throw err;
    }
  }, [bucket, path, expiryDays, setCachedUrl]);

  const refreshUrl = useCallback(async () => {
    if (!enabled || !path) {
      setUrl(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Try cache first
      const cachedUrl = getCachedUrl();
      if (cachedUrl) {
        setUrl(cachedUrl);
        setLoading(false);
        return;
      }

      // Generate new signed URL
      const signedUrl = await generateSignedUrl();
      setUrl(signedUrl);
    } catch (err: any) {
      setError(err.message);
      setUrl(null);
    } finally {
      setLoading(false);
    }
  }, [enabled, path, getCachedUrl, generateSignedUrl]);

  useEffect(() => {
    refreshUrl();
  }, [refreshUrl]);

  return { url, loading, error, refresh: refreshUrl };
};
